import React, { useEffect, useState } from "react";
import { Loader2, ChevronDown, ChevronUp, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiGet, apiPatch } from "@/lib/api";
import { InfluencerTierBadge } from "./InfluencerTierBadge";
import { TierBenefitsCard } from "./TierBenefitsCard";
import type { InfluencerTier, TierId } from "@/lib/tiers";

export interface TierSelectorTarget {
  id: string;
  name: string | null;
  coupon: string | null;
  tier_id: string | null;
}

interface InfluencerTierSelectorProps {
  influencer: TierSelectorTarget | null;
  onOpenChange: (open: boolean) => void;
  /** Called with the new tier id (or null) after a successful change. */
  onChanged: (userId: string, tierId: TierId | null) => void;
}

/**
 * Dialog for admins to assign an influencer's tier (categoria).
 * Self-contained: loads tiers from the API and persists the change itself, so
 * it can be dropped into any admin screen without coupling to its layout.
 */
export const InfluencerTierSelector: React.FC<InfluencerTierSelectorProps> = ({
  influencer,
  onOpenChange,
  onChanged,
}) => {
  const [tiers, setTiers] = useState<InfluencerTier[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedTier, setExpandedTier] = useState<TierId | null>(null);

  const open = !!influencer;

  useEffect(() => {
    if (!open || tiers) return;
    const loadTiers = async () => {
      try {
        const res = await apiGet("/api/influencers/tier");
        if (!res.ok) throw new Error("Erro ao carregar categorias");
        setTiers(await res.json());
        setLoadError(null);
      } catch (err) {
        console.error("Tier load error:", err);
        setLoadError("Erro ao carregar categorias. Tente novamente.");
      }
    };
    loadTiers();
  }, [open, tiers]);

  const assignTier = async (tierId: TierId | null) => {
    if (!influencer) return;
    setSaving(true);
    try {
      const res = await apiPatch("/api/influencers/tier", {
        user_id: influencer.id,
        tier_id: tierId,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || "Erro ao atualizar categoria");
      }
      toast.success(
        tierId ? "Categoria atualizada com sucesso!" : "Categoria removida com sucesso!"
      );
      onChanged(influencer.id, tierId);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro inesperado";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Alterar Categoria</DialogTitle>
        </DialogHeader>
        {influencer && (
          <div className="space-y-4">
            <div>
              <p className="font-medium">{influencer.name || "—"}</p>
              <p className="text-sm text-muted-foreground font-mono">{influencer.coupon || "—"}</p>
            </div>

            {loadError ? (
              <p className="text-sm text-red-400">{loadError}</p>
            ) : !tiers ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-2">
                {tiers.map((tier) => {
                  const isCurrent = influencer.tier_id === tier.id;
                  const isExpanded = expandedTier === tier.id;
                  return (
                    <div key={tier.id} className="space-y-2">
                      <div
                        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 ${
                          isCurrent
                            ? "bg-primary/15 border border-primary/60 shadow-sm"
                            : "bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20"
                        } ${saving ? "opacity-50" : ""}`}
                      >
                        <button
                          onClick={() => assignTier(tier.id)}
                          disabled={saving || isCurrent}
                          className="flex-1 text-left disabled:cursor-default space-y-1"
                        >
                          <span className="flex items-center gap-2">
                            <InfluencerTierBadge tierId={tier.id} />
                            <span className="text-[11px] font-semibold text-primary tabular-nums">
                              {Math.round(tier.commission_rate * 100)}%
                            </span>
                            {isCurrent && (
                              <span className="ml-auto text-[11px] font-medium text-primary">Atual</span>
                            )}
                          </span>
                          <span className="block text-xs text-muted-foreground truncate">
                            {tier.description || `${tier.benefits.length} benefícios`}
                          </span>
                        </button>
                        <button
                          onClick={() => setExpandedTier(isExpanded ? null : tier.id)}
                          className="text-muted-foreground hover:text-primary transition-colors p-1"
                          title="Ver benefícios"
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </div>
                      {isExpanded && <TierBenefitsCard tier={tier} />}
                    </div>
                  );
                })}

                {influencer.tier_id && (
                  <button
                    onClick={() => assignTier(null)}
                    disabled={saving}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors bg-white/5 border border-white/10 hover:bg-white/10 text-muted-foreground ${
                      saving ? "opacity-50" : ""
                    }`}
                  >
                    <XCircle className="h-4 w-4" />
                    Remover categoria
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InfluencerTierSelector;
